import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock'
import * as focusTrap from 'focus-trap' // ESM
import {
	hamburgerButton,
	menuPrimary,
	overlay,
	menuParentItems,
	calendarPopup,
	mqCalendar,
} from './elements.js'
// import { createFocusTrap } from '../../../node_modules/focus-trap/dist/focus-trap.esm.js'

const focusTrape = require('focus-trap')
// CJS
const focusTrapCalendarPopup = focusTrape.createFocusTrap('.js-calendar-popup')

/**
 * Toggle main hamburger menu
 *
 * @author     Mario Hoyos
 * @version    1.0.0
 * @since      2022.1
 */
export function toggleMenu(e) {
	const expanded =
		hamburgerButton.getAttribute('aria-expanded') === 'true' || false
	const menuState = menuPrimary.getAttribute('data-visible') === 'true' || false

	if (
		e.currentTarget === hamburgerButton ||
		((e.currentTarget === overlay || e.keyCode === 27) && expanded)
	) {
		menuPrimary.setAttribute('data-visible', !menuState)
		overlay.setAttribute('data-visible', !menuState)
		hamburgerButton.setAttribute('aria-expanded', !expanded)

		if (expanded) {
			enableBodyScroll(menuPrimary)
		} else {
			disableBodyScroll(menuPrimary)
		}
	}
}

/**
 * Collapse all submenus from primary navigation
 *
 * @author      Mario Hoyos
 * @version     1.0.0
 * @since       2022.1
 * @param       int transitionTiming
 */
function closeAllSubmenus() {
	menuParentItems.forEach((item) => {
		if (item.getAttribute('aria-expanded') === 'true') {
			item.setAttribute('aria-expanded', false)
			item.nextElementSibling.setAttribute('data-collapsed', true)
		}
	})
}

/**
 * Handle main menu clicks to show submenus
 *
 * @author      Mario Hoyos
 * @version     1.0.0
 * @since       2022.1
 */
export function handleMenuClick(e) {
	const isOutside = !e.target.closest('#js-menu-primary')
	if (!e.keyCode) {
		if (isOutside) {
			closeAllSubmenus()
		} else if (e.target.hasAttribute('aria-expanded')) {
			e.preventDefault()
			const menuItem = e.target.closest('[aria-expanded]')
			const isExpanded =
				menuItem.getAttribute('aria-expanded') === 'true' || false
			const subMenu = menuItem.nextElementSibling

			if (isExpanded) {
				menuItem.setAttribute('aria-expanded', false)
				subMenu.setAttribute('data-collapsed', true)
			} else {
				menuParentItems.forEach((item) => {
					if (item !== menuItem) {
						item.setAttribute('aria-expanded', false)
						item.nextElementSibling.setAttribute('data-collapsed', true)
					} else {
						item.setAttribute('aria-expanded', true)
						item.nextElementSibling.setAttribute('data-collapsed', false)
					}
				})
			}
			menuItem.setAttribute('aria-expanded', !isExpanded)
			subMenu.setAttribute('data-collapsed', isExpanded)
		}
	} else {
		closeAllSubmenus()
	}
}

/**
 * Manage spaces navigation events
 *
 * @author     Mario Hoyos
 * @version    1.0.0
 * @since      2022.1
 */
export function navigateSpacesMedia(e) {
	const button = e.currentTarget
	const parent = button.closest('.js-spaces-media')
	const direction = button.getAttribute('data-direction')
	const currentItem = parent.querySelector('[aria-hidden="false"]')
	const currentIndex = Number(currentItem.getAttribute('data-index'))
	const slides = parent.querySelectorAll('figure')
	let newIndex

	if (direction === 'next') {
		if (currentIndex === slides.length - 1) {
			newIndex = 0
		} else {
			newIndex = currentIndex + 1
		}
	} else if (currentIndex === 0) {
		newIndex = slides.length - 1
	} else {
		newIndex = currentIndex - 1
	}

	slides[currentIndex].setAttribute('aria-hidden', 'true')
	slides[newIndex].setAttribute('aria-hidden', 'false')
}

/**
 * Trigger FacetWP filters on enter key press
 *
 * @author      Mario Hoyos
 * @version     1.0.0
 * @since       2022.1
 */
export function triggerFacetWPSearch(e) {
	if (e.which === 13) {
		FWP.refresh()
	}
}

/**
 * Make element sticky only if there is enough vertical space for it
 *
 * @author      Mario Hoyos
 * @version     1.0.0
 * @since       2021.1
 */
export function makeSticky(el) {
	const offset = el.getAttribute('data-offset') || 16
	// Set offset to custom property
	el.style.setProperty('--sticky-top', `${offset}px`)

	if (
		el.clientHeight + parseInt(offset) <
		document.documentElement.clientHeight
	) {
		el.classList.add('u-sticky')
		el.classList.remove('u-non-sticky')
	} else {
		el.classList.remove('u-sticky')
		el.classList.add('u-non-sticky')
	}
}

/**
 * Toggle Calendar events popup on mobile
 *
 * @author      Mario Hoyos
 * @version     1.0.0
 * @since       2021.1
 */
export function openCalendarDialog(e) {
	if (
		!e.target ||
		!e.target.classList.contains('calendar-day') ||
		!mqCalendar.matches
	) {
		return
	}
	e.preventDefault()
	e.stopPropagation()

	const currentDay = e.target
	const events = currentDay.querySelectorAll('.js-event-link')
	const classes = currentDay.classList.value

	const date = extractDateFromString(classes, '---')

	if (events.length != 0) {
		const popupContent = calendarPopup.querySelector('.js-popup-content')
		const popupTitle = calendarPopup.querySelector('.js-popup-title')
		calendarPopup.showModal()
		disableBodyScroll(calendarPopup)
		popupTitle.innerHTML = date
		events.forEach((item) => {
			const clonedEvent = item.cloneNode(true)
			clonedEvent.classList.add('c-event-link--large')
			popupContent.appendChild(clonedEvent)
		})
		setTimeout(() => {
			focusTrapCalendarPopup.activate()
		}, 150)
	}
}

/**
 * Destroy Calendar Popup Data
 *
 * @author      Mario Hoyos
 * @version     1.0.0
 * @since       2022.1
 */
export function destroyCalendarPopup(e) {
	if (
		calendarPopup &&
		(e.keyCode === 27 || e.detail === 1) &&
		mqCalendar.matches
	) {
		const popupContent = calendarPopup.querySelector('.js-popup-content')
		const popupTitle = calendarPopup.querySelector('.js-popup-title')
		enableBodyScroll(calendarPopup)
		focusTrapCalendarPopup.deactivate()
		calendarPopup.close()
		popupContent.innerHTML = ''
		popupTitle.innerHTML = ''
	}
}

/**
 * Extract date from string
 *
 * @author      Mario Hoyos
 * @version     1.0.0
 * @since       2022.1
 */
function extractDateFromString(str, separator) {
	const subStr = str.substring(
		str.indexOf(separator) + separator.length,
		str.lastIndexOf(separator)
	)
	return subStr
}
